/**
 * Instruções de instalação do app (#291)
 *
 * Quando o site não tem o pedido de instalação do navegador para acionar, a tela explica
 * o caminho manual:
 * - iPhone/iPad no Safari (ou Chrome/Edge/Firefox do iPhone): Compartilhar › Adicionar à
 *   Tela de Início;
 * - iPhone/iPad dentro do navegador embutido de outro app (Instagram, Facebook...): lá
 *   não existe "Adicionar à Tela de Início", então pede para abrir a página no Safari;
 * - Android e computador: menu do navegador › Instalar app / Adicionar à tela inicial.
 * Usado no convite da loja, no link do rodapé da loja e na opção "Instalar o app Vende
 * Aí" do painel.
 *
 * @author Equipe VendeAi
 * @since 2.4.0
 */

import { Share, PlusSquare, MoreVertical, Compass } from 'lucide-react';
import { Modal } from '@components/ui/Modal';
import { isIOSDevice, isIOSInAppBrowser } from '@utils/pwaInstall';

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span
        className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-700"
        aria-hidden="true"
      >
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

/** Dentro do Instagram/Facebook/etc. no iPhone: abrir no Safari primeiro */
function IOSInAppBrowserSteps({ appName }: { appName: string }) {
  return (
    <ol className="space-y-2 text-left text-sm text-gray-700">
      <Step n={1}>
        Toque no menu <strong>…</strong> (no canto da tela) e escolha{' '}
        <strong>Abrir no Safari</strong>{' '}
        <Compass size={14} className="inline align-text-bottom" aria-hidden="true" />. Este
        navegador, dentro de outro app, não instala apps.
      </Step>
      <Step n={2}>
        No Safari, toque em <strong>Compartilhar</strong>{' '}
        <Share size={14} className="inline align-text-bottom" aria-hidden="true" /> e escolha{' '}
        <strong>Adicionar à Tela de Início</strong>.
      </Step>
      <Step n={3}>
        Toque em <strong>Adicionar</strong>. O ícone de <strong>{appName}</strong> aparece na tela
        de início.
      </Step>
    </ol>
  );
}

/**
 * Passo a passo do iPhone/iPad (Compartilhar › Adicionar à Tela de Início). Dentro do
 * navegador embutido de outro app, mostra antes o "Abrir no Safari".
 */
export function IOSInstallSteps({ appName }: { appName: string }) {
  if (isIOSInAppBrowser()) return <IOSInAppBrowserSteps appName={appName} />;
  return (
    <ol className="space-y-2 text-left text-sm text-gray-700">
      <Step n={1}>
        Toque em <strong>Compartilhar</strong>{' '}
        <Share size={14} className="inline align-text-bottom" aria-hidden="true" /> na barra do
        navegador.
      </Step>
      <Step n={2}>
        Escolha <strong>Adicionar à Tela de Início</strong>{' '}
        <PlusSquare size={14} className="inline align-text-bottom" aria-hidden="true" />.
      </Step>
      <Step n={3}>
        Toque em <strong>Adicionar</strong>. O ícone de <strong>{appName}</strong> aparece na tela
        de início.
      </Step>
    </ol>
  );
}

/** Android e computador: pelo menu do navegador */
export function BrowserInstallSteps({ appName }: { appName: string }) {
  return (
    <ol className="space-y-2 text-left text-sm text-gray-700">
      <Step n={1}>
        Abra o menu do navegador{' '}
        <MoreVertical size={14} className="inline align-text-bottom" aria-hidden="true" /> (os três
        pontinhos no canto da tela).
      </Step>
      <Step n={2}>
        Escolha <strong>Instalar app</strong> (no computador, às vezes em{' '}
        <strong>Transmitir, salvar e compartilhar</strong>) ou{' '}
        <strong>Adicionar à tela inicial</strong> (no celular).
      </Step>
      <Step n={3}>
        Confirme em <strong>Instalar</strong>. O app <strong>{appName}</strong> passa a abrir pelo
        ícone, sem a barra do navegador.
      </Step>
      <li className="pt-1 text-xs text-gray-500">
        Não achou a opção? Seu navegador pode não instalar apps: use o Chrome ou o Edge.
      </li>
    </ol>
  );
}

/** Passo a passo certo para o aparelho atual */
export function InstallSteps({ appName }: { appName: string }) {
  return isIOSDevice() ? (
    <IOSInstallSteps appName={appName} />
  ) : (
    <BrowserInstallSteps appName={appName} />
  );
}

interface InstallInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nome do app (loja ou "Vende Aí") */
  appName: string;
}

/** Janela com o passo a passo de instalação do aparelho atual */
export function InstallInstructionsModal({
  isOpen,
  onClose,
  appName,
}: InstallInstructionsModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Instalar o app ${appName}`}
      size="sm"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
        >
          Entendi
        </button>
      }
    >
      <div className="rounded-lg bg-white p-1">
        <InstallSteps appName={appName} />
      </div>
    </Modal>
  );
}
